import { Flex } from '@chakra-ui/react'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { ShieldCheck } from 'lucide-react'
import { Key, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { StorageKeys } from '~/atoms/StorageKeys'
import { useUserState } from '~/atoms/UserState'
import { ToggleListButton } from '~/components/layout/ResponsivePage'
import { useChangelogModal } from '~/hooks/useChangelogModal'
import { useCheckRole } from '~/hooks/useCheckRole'
import { useShowTexting } from '~/hooks/useCurrentPartner'
import useElectronIPC from '~/hooks/useElectronIPC'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { setAdminReturnTo } from '~/pages/Admin/adminReturnTo'
import { useAdminMenuItems } from '~/pages/Admin/useAdminMenuItems'
import {
  SiteNavigationSizes,
  useSiteNavigationStyle,
} from '~/pages/Home/SiteNavigation/componentStyles/SiteNavigation.style'
import { SiteNavigationCollapseTab } from '~/pages/Home/SiteNavigation/shared/SiteNavigationCollapseTab'
import { SiteNavigationTab } from '~/pages/Home/SiteNavigation/shared/SiteNavigationTab'
import { CallsTab } from '~/pages/Home/SiteNavigation/tabImplementations/CallsTab'
import { ContactsTab } from '~/pages/Home/SiteNavigation/tabImplementations/ContactsTab'
import { FaxTab } from '~/pages/Home/SiteNavigation/tabImplementations/FaxTab'
import { HotelsTab } from '~/pages/Home/SiteNavigation/tabImplementations/HotelsTab'
import { MessagingTab } from '~/pages/Home/SiteNavigation/tabImplementations/MessagingTab'
import { MissedCallsTab } from '~/pages/Home/SiteNavigation/tabImplementations/MissedCallsTab'
import { VoicemailTab } from '~/pages/Home/SiteNavigation/tabImplementations/VoicemailTab'
import { WebPhoneTab } from '~/pages/Home/SiteNavigation/tabImplementations/WebPhoneTab'
import { useHotelsPermissions } from '~/pages/Hotels/useHotelsPermissions'
import { ActiveCallsBlock } from '~/pages/WebPhone/ActiveCallBlock'
import { isUserAdmin } from '~/utils/auth'
import { isElectron } from '~/utils/common'
import { shouldUseHotelsExperience } from '~/utils/pbxUtils'

import { isNavRouteSelected } from './isNavRouteSelected'
import { ReportingTab } from './tabImplementations/ReportingTab'

interface SiteNavigationLocalStorage {
  isUserCollapsed?: boolean
}

const navigationLocalStorage = atomWithStorage<SiteNavigationLocalStorage>(
  StorageKeys.SiteNav,
  {
    isUserCollapsed: undefined,
  },
)

export const SiteNavigation = () => {
  const {
    initDeepLinkListener,
    initSettingsHotkeyListener,
    initGetCallPopSettingsListener,
    initGetCopyNumberHotKeyResultListener,
    initGoToMessagingListener,
    initGetPopupWindowDimensionsListener,
    initSetPopupWindowDimensionsInStorageListener,
  } = useElectronIPC()
  const { showChangelog, ChangelogModal } = useChangelogModal()
  const location = useLocation()
  const pathname = location.pathname
  const [_ls, _setLs] = useAtom(navigationLocalStorage)

  const {
    enhancedCallLogsEnabled: hasNewCallsAccess,
    agentStatusesEnabled: callCenterEnabled,
    pbx,
    userRoles,
  } = useUserState({
    canAutoRefresh: true,
  })
  const showTexting = useShowTexting()

  const { hasAdminItems } = useAdminMenuItems()
  const { permissions: hotelsPermissions, hasAnyAccess: hasAnyHotelAccess } =
    useHotelsPermissions()
  const checkRole = useCheckRole()
  const showAdminTab = hasAdminItems
  const { t } = useTranslation()

  const isUserCollapsed = _ls.isUserCollapsed
  const setIsUserCollapsed = (isCollapsed?: boolean) => {
    _setLs({ isUserCollapsed: isCollapsed })
  }

  const isCallCenterAdmin = callCenterEnabled && isUserAdmin(userRoles)
  const showReportingTab = checkRole('admin')
  const showHotelsTab = shouldUseHotelsExperience({ pbx }) && hasAnyHotelAccess

  //prettier-ignore
  const isCollapsed =
    isUserCollapsed !== undefined
      ? isUserCollapsed
      : false

  const getSiteNavigationSize = () => {
    if (isCollapsed) {
      return SiteNavigationSizes.Collapsed
    }
    return SiteNavigationSizes.Normal
  }

  const style = useSiteNavigationStyle({
    size: getSiteNavigationSize(),
  })

  const navigate = useNavigate()

  const handleSetSelectedTab = (id?: Key) => {
    if (id === ApplicationRoutes.ADMIN) {
      setAdminReturnTo(window.location.pathname + window.location.search)
      navigate(ApplicationRoutes.ADMIN)
      return
    }
    navigate(id as ApplicationRoutes)
  }

  const handleCollapseClicked = () => {
    setIsUserCollapsed(true)
  }

  const handleExpandClicked = () => {
    setIsUserCollapsed(undefined)
  }

  useEffect(() => {
    if (!isElectron) {
      return
    }

    const removeDeepLinkListener = initDeepLinkListener(navigate, {
      onChangelog: showChangelog,
    })
    const removeSettingsHotkeyListener = initSettingsHotkeyListener(navigate)
    const removeGetCallPopSettingsListener = initGetCallPopSettingsListener()
    const removeGetCopyNumberHotKeyListener =
      initGetCopyNumberHotKeyResultListener()
    const removeGoToMessagingListener = initGoToMessagingListener(navigate)
    const removeGetPopupWindowDimensionsListener =
      initGetPopupWindowDimensionsListener()
    const removeSetPopupWindowDimensionsListener =
      initSetPopupWindowDimensionsInStorageListener()

    return () => {
      removeDeepLinkListener()
      removeSettingsHotkeyListener()
      removeGetCallPopSettingsListener()
      removeGetCopyNumberHotKeyListener()
      removeGoToMessagingListener()
      removeGetPopupWindowDimensionsListener()
      removeSetPopupWindowDimensionsListener()
    }
  }, [])

  const pathCheck = (route: string) => isNavRouteSelected(pathname, route)
  const callsTabContent = useMemo(() => {
    return (
      <CallsTab
        id={ApplicationRoutes.CALLS}
        isSelected={pathCheck(ApplicationRoutes.CALLS)}
        onClick={handleSetSelectedTab}
        isCollapsed={isCollapsed}
      />
    )
  }, [hasNewCallsAccess, pathname, isCollapsed])

  const siteNavigationTabs = useMemo(() => {
    return (
      <>
        {showHotelsTab && (
          <HotelsTab
            isCollapsed={isCollapsed}
            pathname={pathname}
            canViewDashboard={Boolean(hotelsPermissions.canViewDashboard)}
            canViewSettings={Boolean(hotelsPermissions.canViewSettings)}
            canViewScheduler={Boolean(hotelsPermissions.canViewScheduler)}
            canViewHistory={Boolean(hotelsPermissions.canViewHistory)}
            onNavigate={(path) => navigate(path)}
          />
        )}
        {callsTabContent}
        <MissedCallsTab
          id={ApplicationRoutes.MISSED_CALLS}
          isSelected={pathCheck(ApplicationRoutes.MISSED_CALLS)}
          onClick={handleSetSelectedTab}
          isCollapsed={isCollapsed}
        />
        <VoicemailTab
          id={ApplicationRoutes.VOICEMAIL}
          isSelected={pathCheck(ApplicationRoutes.VOICEMAIL)}
          onClick={handleSetSelectedTab}
          isCollapsed={isCollapsed}
        />
        {showTexting && (
          <MessagingTab
            id={ApplicationRoutes.MESSAGING}
            isSelected={pathCheck(ApplicationRoutes.MESSAGING)}
            onClick={handleSetSelectedTab}
            isCollapsed={isCollapsed}
          />
        )}
        <FaxTab
          id={ApplicationRoutes.FAX}
          isSelected={pathCheck(ApplicationRoutes.FAX)}
          onClick={handleSetSelectedTab}
          isCollapsed={isCollapsed}
        />
        <ContactsTab
          id={ApplicationRoutes.CONTACTS}
          isSelected={pathCheck(ApplicationRoutes.CONTACTS)}
          onClick={handleSetSelectedTab}
          isCollapsed={isCollapsed}
        />
        <WebPhoneTab
          id={ApplicationRoutes.WEB_PHONE}
          isSelected={pathCheck(ApplicationRoutes.WEB_PHONE)}
          onClick={handleSetSelectedTab}
          isCollapsed={isCollapsed}
        />
        {showReportingTab && (
          <ReportingTab
            isCollapsed={isCollapsed}
            pathname={pathname}
            callCenterEnabled={isCallCenterAdmin}
            onNavigate={(path) => navigate(path)}
          />
        )}
      </>
    )
  }, [
    isCollapsed,
    callsTabContent,
    pathname,
    isCallCenterAdmin,
    showHotelsTab,
    showReportingTab,
    hotelsPermissions.canViewDashboard,
    hotelsPermissions.canViewSettings,
    hotelsPermissions.canViewScheduler,
    hotelsPermissions.canViewHistory,
    showTexting,
  ])

  return (
    <>
      {ChangelogModal}
      <Flex asChild css={style.container}>
        <nav aria-label={t('nav.mainNavigation', 'Main navigation')}>
          <Flex css={style.tabWrapper}>
            <ToggleListButton />
            {siteNavigationTabs}
          </Flex>

          <Flex css={style.footerWrapper}>
            <ActiveCallsBlock isCollapsed={isCollapsed} />
            {showAdminTab && (
              <SiteNavigationTab
                id={ApplicationRoutes.ADMIN}
                title={t('baseSettings.admin', 'Admin')}
                icon={ShieldCheck}
                isSelected={pathname.startsWith('/admin')}
                isCollapsed={isCollapsed}
                onClick={() => handleSetSelectedTab(ApplicationRoutes.ADMIN)}
              />
            )}
            <SiteNavigationCollapseTab
              id={ApplicationRoutes.SETTINGS}
              isCollapsed={isCollapsed}
              isSelected={pathCheck(ApplicationRoutes.SETTINGS)}
              hideExpand={false}
              onCollapseClicked={handleCollapseClicked}
              onExpandClicked={handleExpandClicked}
              onSettingsClicked={handleSetSelectedTab}
            />
          </Flex>
        </nav>
      </Flex>
    </>
  )
}
